import { useEffect, useState } from 'react'
import { Screen } from '../components/Screen'
import { Field } from '../components/Form'
import { SportFieldsPreview, SportSelect, useSportTypes } from '../components/SportFields'
import { ErrorState, Skeleton } from '../components/States'
import { useEnv } from '../context'
import type { SportOption } from '../data/types'
import { EMPTY_QUICK_SCHEDULE, QuickScheduleFields, quickScheduleError, quickSlots, type QuickSchedule } from './GroupForms'

type Step = 'club' | 'direction' | 'group'
const STEP_NO: Record<Step, number> = { club: 1, direction: 2, group: 3 }

/**
 * Подсказка в мини-приложении MAX: новая учётка MAX получает пустой клуб, и
 * тренер, который уже работает в Telegram, может завести здесь второй. Пока
 * направление не создано, привязка проходит (пустой аккаунт MAX вливается в
 * аккаунт Telegram); после - откажет: данные есть у обоих. Поэтому подсказка
 * стоит на первом шаге, до создания направления. Шаги - как работает
 * привязка: код выдаёт /link в Telegram-боте, вводят его /link в боте MAX
 * (accountlink, docs/design/max-bot.md).
 */
function MaxLinkHint() {
  const { platform } = useEnv()
  const botUrl = platform.botLink()
  const [openFailed, setOpenFailed] = useState(false)
  if (platform.kind !== 'max') return null
  return (
    <section className="pc-note" aria-labelledby="pc-max-link-title" style={{ marginTop: 16 }}>
      <h2 id="pc-max-link-title" style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
        Уже ведёте клуб в Telegram?
      </h2>
      <p style={{ margin: '4px 0 0' }}>Привяжите аккаунт командой /link в боте, чтобы не завести второй клуб.</p>
      <ol style={{ margin: '8px 0 0', paddingLeft: 20, listStyle: 'decimal' }}>
        <li>
          В Telegram-боте ProCoach отправьте <b>/link</b> - бот пришлёт код из 6 цифр. Код действует 10 минут.
        </li>
        <li style={{ marginTop: 4 }}>
          Боту ProCoach здесь, в MAX, отправьте <b>/link</b> и этот код, например: <b>/link 123456</b>.
        </li>
        <li style={{ marginTop: 4 }}>Закройте приложение и откройте снова - откроется ваш клуб.</li>
      </ol>
      {botUrl && (
        <button
          type="button"
          className="pc-btn pc-btn--block"
          style={{ marginTop: 12 }}
          onClick={() => setOpenFailed(!platform.openBotLink(botUrl))}
        >
          Открыть бота ProCoach в MAX
        </button>
      )}
      {openFailed && (
        <p role="alert" style={{ margin: '8px 0 0' }}>
          Не получилось открыть бота. Найдите ProCoach в списке чатов MAX.
        </p>
      )}
    </section>
  )
}

/**
 * Мастер первого запуска (S01, S02, SC01): клуб -> направление -> первая
 * группа. Пользователь видит, что создаёт, и какие поля даст вид спорта, до
 * заполнения карточек. Созданное запоминается: возврат на шаг назад или
 * повторное нажатие не создают второе направление или группу.
 */
export function SetupScreen({ onDone, initialClubName = '' }: { onDone: () => void; initialClubName?: string }) {
  const { api, toast } = useEnv()
  const [step, setStep] = useState<Step>('club')
  const [mode, setMode] = useState<'create' | 'join'>('create')
  const [clubName, setClubName] = useState(initialClubName)
  const [renamedTo, setRenamedTo] = useState(initialClubName)
  const [code, setCode] = useState('')
  const [invitePreview, setInvitePreview] = useState<string | null>(null)
  const [sportName, setSportName] = useState('')
  const [directionName, setDirectionName] = useState('')
  const [direction, setDirection] = useState<{ id: string; name: string } | null>(null)
  const [groupName, setGroupName] = useState('')
  const [quick, setQuick] = useState<QuickSchedule>(EMPTY_QUICK_SCHEDULE)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sports = useSportTypes()
  const sport: SportOption | undefined = sports.data?.find((s) => s.name === sportName)

  useEffect(() => setClubName((v) => v || initialClubName), [initialClubName])
  useEffect(() => setError(null), [step, mode])

  const run = async (call: () => Promise<void>, failText: string) => {
    setBusy(true)
    setError(null)
    try {
      await call()
    } catch (e) {
      const kind = (e as { kind?: string })?.kind
      setError(kind === 'not_found' && mode === 'join' ? 'Код не найден, истёк или уже использован.' : failText)
    } finally {
      setBusy(false)
    }
  }

  const back = step === 'club' ? undefined : () => setStep(step === 'group' ? 'direction' : 'club')

  let body
  if (step === 'club') {
    body = (
      <>
        <MaxLinkHint />
        <h2 className="pc-section-title" style={{ marginTop: 16 }}>
          Где вы будете работать?
        </h2>
        <div role="radiogroup" aria-label="Клуб" style={{ display: 'grid', gap: 8 }}>
          {(
            [
              ['create', 'Создать свой клуб', 'Для самостоятельного тренера или руководителя секции.'],
              ['join', 'Присоединиться по коду', 'Если вас пригласил руководитель.'],
            ] as const
          ).map(([m, title, text]) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              className="pc-card"
              style={{
                textAlign: 'left',
                cursor: 'pointer',
                borderColor: mode === m ? 'var(--pc-accent)' : undefined,
                boxShadow: mode === m ? '0 0 0 1px var(--pc-accent)' : undefined,
                font: 'inherit',
                color: 'inherit',
              }}
              onClick={() => setMode(m)}
            >
              <div style={{ fontWeight: 700, fontSize: 17 }}>{title}</div>
              <div className="pc-muted" style={{ fontSize: 14, marginTop: 2 }}>
                {text}
              </div>
            </button>
          ))}
        </div>

        {mode === 'create' ? (
          <div className="pc-form">
            <Field label="Название клуба" error={error ?? undefined}>
              {(p) => <input {...p} className="pc-input" value={clubName} onChange={(e) => setClubName(e.target.value)} />}
            </Field>
            <button
              type="button"
              className="pc-btn pc-btn--primary pc-btn--block"
              disabled={busy || !clubName.trim()}
              onClick={() =>
                void run(async () => {
                  // Повторное нажатие с тем же названием на сервер не ходит.
                  if (clubName.trim() !== renamedTo) {
                    await api.renameClub(clubName.trim())
                    setRenamedTo(clubName.trim())
                  }
                  setStep('direction')
                }, 'Не удалось сохранить название. Попробуйте ещё раз.')
              }
            >
              {busy ? 'Сохраняю…' : 'Продолжить'}
            </button>
          </div>
        ) : (
          <div className="pc-form">
            <Field label="Код приглашения" hint="Его присылает руководитель клуба" error={error ?? undefined}>
              {(p) => (
                <input
                  {...p}
                  className="pc-input"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value)
                    setInvitePreview(null)
                  }}
                  autoCapitalize="characters"
                  autoComplete="off"
                />
              )}
            </Field>
            {invitePreview ? (
              <>
                <div className="pc-note">
                  Вы вступаете в клуб «{invitePreview}» как тренер с полным доступом: все направления и группы клуба.
                </div>
                <button
                  type="button"
                  className="pc-btn pc-btn--primary pc-btn--block"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await api.acceptInvite(code)
                      toast(`Вы в клубе «${invitePreview}»`)
                      onDone()
                    }, 'Не удалось вступить. Попробуйте ещё раз.')
                  }
                >
                  {busy ? 'Вступаю…' : 'Вступить'}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="pc-btn pc-btn--primary pc-btn--block"
                disabled={busy || !code.trim()}
                onClick={() =>
                  void run(async () => {
                    const res = await api.previewInvite(code)
                    setInvitePreview(res.clubName)
                  }, 'Не удалось проверить код. Попробуйте ещё раз.')
                }
              >
                {busy ? 'Проверяю…' : 'Проверить код'}
              </button>
            )}
          </div>
        )}
      </>
    )
  } else if (step === 'direction') {
    body = direction ? (
      <>
        <div className="pc-note">Направление «{direction.name}» уже создано. Его можно переименовать позже в настройках клуба.</div>
        <button type="button" className="pc-btn pc-btn--primary pc-btn--block" style={{ marginTop: 16 }} onClick={() => setStep('group')}>
          Продолжить
        </button>
      </>
    ) : sports.isPending ? (
      <Skeleton rows={2} label="Загружаю виды спорта" />
    ) : sports.isError ? (
      <ErrorState title="Не удалось загрузить виды спорта" onRetry={() => void sports.refetch()} />
    ) : (
      <div className="pc-form">
        <SportSelect
          sports={sports.data}
          value={sportName}
          onChange={(name) => {
            setSportName(name)
            if (!directionName || directionName === sportName) setDirectionName(name)
          }}
        />
        <Field label="Название направления" hint="Например: «Каратэ Киокушинкай» или «ОФП для малышей»">
          {(p) => <input {...p} className="pc-input" value={directionName} onChange={(e) => setDirectionName(e.target.value)} />}
        </Field>

        {sportName && <SportFieldsPreview sportName={sportName} />}

        {error && (
          <div className="pc-alert" role="alert">
            {error}
          </div>
        )}
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          disabled={busy || !sport || !directionName.trim()}
          onClick={() =>
            void run(async () => {
              const created = await api.createDirection(directionName.trim(), sport!)
              setDirection({ id: created.id, name: directionName.trim() })
              setStep('group')
            }, 'Не удалось создать направление. Попробуйте ещё раз.')
          }
        >
          {busy ? 'Создаю…' : 'Продолжить'}
        </button>
      </div>
    )
  } else {
    const timeError = quickScheduleError(quick)
    body = (
      <div className="pc-form">
        <Field label="Название группы" hint="Например: «Стрижи» или «Младшая группа»">
          {(p) => <input {...p} className="pc-input" value={groupName} onChange={(e) => setGroupName(e.target.value)} />}
        </Field>
        <QuickScheduleFields value={quick} onChange={setQuick} />
        {error && (
          <div className="pc-alert" role="alert">
            {error}
          </div>
        )}
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          disabled={busy || !groupName.trim() || !!timeError}
          onClick={() =>
            void run(async () => {
              await api.createGroup(groupName.trim(), quickSlots(quick))
              toast('Клуб настроен')
              onDone()
            }, 'Не удалось создать группу. Попробуйте ещё раз.')
          }
        >
          {busy ? 'Создаю…' : 'Создать группу'}
        </button>
        <button type="button" className="pc-btn pc-btn--block" disabled={busy} onClick={onDone}>
          Отложить
        </button>
      </div>
    )
  }

  const titles: Record<Step, string> = { club: 'Начнём с клуба', direction: 'Направление', group: 'Первая группа' }
  return (
    <Screen title={titles[step]} back={back} hideHub hideNav>
      <p className="pc-muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
        Настройка рабочего пространства
      </p>
      <p className="pc-badge" style={{ marginTop: 8 }} aria-live="polite">
        Шаг {STEP_NO[step]} из 3
      </p>
      {body}
    </Screen>
  )
}
